'use client';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from 'components/ui/button';
import { Accordion } from 'components/ui/accordion';
import { UserQuery } from 'generated/operations';
import { CompanyExperienceNode, CompanyNode, UpdateExperienceMutation } from 'generated/types';
import { useDeleteUserExperienceMutation } from 'generated/hooks';
import { useToast } from 'hooks/useToast';
import { TabIds } from './ProfileForm';
import { ExperienceCard, ExperienceNodeFromBe, IExperienceForm } from './ExperienceCard';
import { months } from 'utils/date';

type EditUserNode = NonNullable<UserQuery['user']>;

interface IExperience {
  companies: CompanyNode[];
  currentTab: TabIds;
  user: EditUserNode;
  onSetDirtyForm: (formName: TabIds, isDirty: boolean, userData?: EditUserNode) => void;
  onUpdateUser: (user: EditUserNode) => void;
}

export const Experience: FC<IExperience> = (props) => {
  const {
    companies,
    currentTab,
    user,
    onSetDirtyForm,
    onUpdateUser
  } = props;

  const [, deleteUserExperience] = useDeleteUserExperienceMutation();

  const [savedExperiencesFromBe, setSavedExperiencesFromBe] = useState<CompanyExperienceNode[]>(user.companyExperience ?? []);

  const { handleToast } = useToast();

  const [accordionValues, setValues] = useState<string[]>(user.companyExperience?.length ? ['1'] : []);

  const [savedExperiences, setExperience] = useState<{ experiences: (IExperienceForm & { localId?: number })[] }>({
    experiences: savedExperiencesFromBe.map(experienceItem => ({
      id: experienceItem.id,
      company: {
        id: experienceItem.company?.id ?? '',
        name: experienceItem.company?.name ?? '',
      },
      positionRaw: experienceItem.positionRaw ?? '',
      fromMonth: months.find(month => month.value === (experienceItem.fromMonth ? experienceItem.fromMonth - 1 : null)) || null,
      fromYear: experienceItem.fromYear ? {
        value: experienceItem.fromYear,
        title: experienceItem.fromYear.toString()
      } : { value: 0, title: '' },
      toMonth: months.find(month => month.value === (experienceItem.toMonth ? experienceItem.toMonth - 1 : null)) ?? null,
      toYear: experienceItem.toYear ? {
        value: experienceItem.toYear,
        title: experienceItem.toYear.toString()
      } : null,
      description: experienceItem.description ?? '',
      isCurrentlyWorkingHere: !experienceItem.toMonth && !experienceItem.toYear
    })) ?? []
  });

  const [dirtyCards, setDirtyCards] = useState<Record<string, boolean>>(user.companyExperience?.reduce((acc: Record<string, boolean>, item) => {
    acc[item.id] = false;
    return acc;
  }, {}) ?? {});

  const handleUpdate = useCallback((experience: IExperienceForm & {
      localId?: number
    }, experienceFromBE: ExperienceNodeFromBe) => {
      setExperience(prevState => {
        const newValue = prevState.experiences.map(item => {
          if (item.id === experience.id || (!!item.localId && !!experience.localId && item.localId === experience.localId)) {
            return ({
              ...item,
              ...experience
            });
          }

          return item;
        });

        return ({
          experiences: [...newValue]
        });
      });

      if (experienceFromBE) {
        setSavedExperiencesFromBe(prevState => {
          const index = prevState.findIndex(item => item.id === experienceFromBE.id);
          if (index === -1) {
            return [...prevState, experienceFromBE];
          }
          return prevState.map(item =>
            item.id === experienceFromBE.id ? { ...item, ...experienceFromBE } : item
          );
        });
      }
    }, []
  );

  const handleDelete = useCallback(async (experience: IExperienceForm & { localId?: number }) => {
    if (!experience.id && experience.localId) {
      setExperience(prevState => {
        const newArray = [...prevState.experiences.filter((item) => item?.localId !== experience.localId)];

        setDirtyCards(prevState => {
          const newState = { ...prevState };

          if (experience.localId) {
            delete newState[experience.localId.toString()];
          }

          if (experience.id) {
            delete newState[experience.id.toString()];
          }

          return { ...newState };
        });

        return ({
          experiences: [...newArray]
        });
      });
    } else {
      const response = await deleteUserExperience({
        id: experience.id as string
      });

      if (response.error) {
        handleToast(response.error.message, 'error');
        console.error(response.error);
        return;
      }

      setExperience(prevState => {
        const newValues = prevState.experiences.filter(item => item.id !== experience.id);

        return {
          experiences: newValues
        };
      });

      onUpdateUser({
        ...user,
        companyExperience:
          savedExperiences.experiences
            .filter(item => item.id !== experience.id)
            .map(({ localId, ...item }) => ({
              ...item,
              id: item.id ?? '',
              fromYear: item.fromYear?.value ?? 0,
              toYear: item.toYear?.value ?? 0,
              fromMonth: item.fromMonth?.value ?? 0,
              toMonth: item.toMonth?.value ?? 0
            }))
      });
      setSavedExperiencesFromBe(prevState => prevState.filter(item => item.id !== experience.id));
    }

    handleToast('Experience successfully deleted!', 'success');
  }, [deleteUserExperience, handleToast, onUpdateUser, savedExperiences.experiences, user]);

  const isDirty = useMemo(() => {
    return Object.values(dirtyCards).some(item => item);
  }, [dirtyCards]);

  const handleCardDirty = useCallback((id: string, isDirty: boolean) => {
    setDirtyCards(prevState => {
      return {
        ...prevState,
        [id]: isDirty
      };
    });
  }, []);

  useEffect(() => {
    onSetDirtyForm(TabIds.EXPERIENCE, isDirty);
  }, [isDirty, onSetDirtyForm]);

  useEffect(() => {
    if (currentTab !== TabIds.EXPERIENCE) {
      onSetDirtyForm(TabIds.EXPERIENCE, false, {
        ...user,
        companyExperience: savedExperiencesFromBe
      });
    }
  }, [currentTab, onSetDirtyForm, savedExperiencesFromBe, user]);

  return (
    <div className="flex flex-col gap-10">
      <Accordion type="multiple" value={accordionValues} onValueChange={(value) => setValues(() => [...value])}>
        {savedExperiences.experiences.map((experiences, index, array) => (
          <ExperienceCard
            isOpen={accordionValues.includes((index + 1).toString())}
            key={experiences.id || experiences.localId || index}
            companies={companies}
            experienceItem={experiences}
            index={index}
            isLastCard={array.length - 1 === index}
            onUpdateItem={handleUpdate}
            onDelete={handleDelete}
            onSetDirtyCard={handleCardDirty}
          />
        ))}
      </Accordion>
      <Button
        variant="nakedGrayLink"
        onClick={() => {
          const localId = new Date().getTime();

          setExperience(prevState => {
            const newValue = {
              experiences: [...prevState.experiences, {
                id: '',
                company: { id: '', name: '' },
                description: '',
                positionRaw: '',
                fromYear: {
                  value: new Date().getFullYear(),
                  title: new Date().getFullYear().toString()
                },
                toMonth: null,
                toYear: null,
                fromMonth: null,
                isCurrentlyWorkingHere: false,
                localId
              }]
            };

            handleCardDirty(localId.toString(), true);

            setValues(prevState => [...new Set([...prevState, newValue.experiences.length.toString()])]);

            return newValue;
          });
        }}
      >
        Add new job +
      </Button>
    </div>
  );
};
